/**
 * Validación Cruzada Condicional
 * 
 * GEO-06: Decide si un registro necesita validación cruzada multi-fuente
 * basándose en tipología, formato y calidad de coordenadas.
 * 
 * PRINCIPIO: No gastar recursos validando datos que ya son confiables
 * 
 * IMPACTO ESPERADO:
 * - +20% velocidad de procesamiento
 * - -40% consultas WFS innecesarias
 * - Mantiene 100% validación para infraestructura crítica
 * 
 * @module services/geocoding/conditionalValidation
 */

import type { DocumentFormat } from '@/utils/formatDetector';
import { CRITICAL_INFRASTRUCTURE } from './validationStrategy';

// ============================================================================
// TIPOS
// ============================================================================

/** Razón de decisión de validación */
export type ValidationDecisionReason =
  | 'CRITICAL_INFRASTRUCTURE'  // Siempre validar: hospital, bomberos, etc.
  | 'LOW_SCORE'               // Score bajo, necesita validación
  | 'PROBLEMATIC_FORMAT'      // Formato problemático (ODT, HTML)
  | 'HIGH_QUALITY_SKIP'       // Alta calidad, se puede omitir
  | 'STANDARD_CHECK'          // Caso normal, validar
  | 'MISSING_DATA';           // Faltan datos, no se puede validar

/** Resultado de la decisión de validación */
export interface ValidationDecision {
  /** Si debe realizarse validación cruzada */
  shouldValidate: boolean;
  
  /** Razón de la decisión */
  reason: ValidationDecisionReason;
  
  /** Explicación legible */
  explanation: string;
  
  /** Prioridad de validación (1=alta, 3=baja) */
  priority: 1 | 2 | 3;
  
  /** Factores considerados */
  factors: string[];
}

/** Registro PTEL para evaluar */
export interface PTELRecordForValidation {
  /** Tipología de infraestructura */
  tipologia?: string;
  
  /** Formato del documento origen */
  formato?: DocumentFormat;
  
  /** Score de las coordenadas (0-100) */
  coordScore?: number;
  
  /** Si tiene coordenadas válidas */
  hasCoordinates?: boolean;
  
  /** Si tiene dirección */
  hasAddress?: boolean;
  
  /** Nombre de la infraestructura */
  nombre?: string;
  
  /** Municipio */
  municipio?: string;
}

/** Configuración de validación condicional */
export interface ConditionalValidationConfig {
  /** Score mínimo para omitir validación en texto plano */
  plainTextSkipScore: number;
  
  /** Score mínimo para omitir validación en spreadsheet */
  spreadsheetSkipScore: number;
  
  /** Score por debajo del cual siempre validar */
  alwaysValidateBelowScore: number;
  
  /** Siempre validar infraestructura crítica */
  alwaysValidateCritical: boolean;
  
  /** Siempre validar formatos problemáticos */
  alwaysValidateProblematicFormats: boolean;
}

/** Estadísticas de decisiones de validación */
export interface ValidationStats {
  /** Total de registros evaluados */
  total: number;
  
  /** Registros que requieren validación */
  requireValidation: number;
  
  /** Registros que se pueden saltar */
  skipped: number;
  
  /** Porcentaje de ahorro */
  savingsPercent: number;
  
  /** Desglose por razón */
  byReason: Record<ValidationDecisionReason, number>;
}

// ============================================================================
// CONFIGURACIÓN
// ============================================================================

/**
 * Configuración por defecto
 */
export const DEFAULT_VALIDATION_CONFIG: ConditionalValidationConfig = {
  plainTextSkipScore: 90,
  spreadsheetSkipScore: 88,
  alwaysValidateBelowScore: 70,
  alwaysValidateCritical: true,
  alwaysValidateProblematicFormats: true,
};

/**
 * Configuración agresiva (más ahorro, menos validación)
 */
export const AGGRESSIVE_SKIP_CONFIG: ConditionalValidationConfig = {
  plainTextSkipScore: 85,
  spreadsheetSkipScore: 82,
  alwaysValidateBelowScore: 60,
  alwaysValidateCritical: true,  // Críticos siempre
  alwaysValidateProblematicFormats: false,
};

/**
 * Configuración conservadora (más validación, menos ahorro)
 */
export const CONSERVATIVE_CONFIG: ConditionalValidationConfig = {
  plainTextSkipScore: 95,
  spreadsheetSkipScore: 92,
  alwaysValidateBelowScore: 80,
  alwaysValidateCritical: true,
  alwaysValidateProblematicFormats: true,
};

/**
 * Formatos considerados problemáticos que siempre requieren validación extra
 */
export const PROBLEMATIC_FORMATS: DocumentFormat[] = ['odt', 'html'];

// ============================================================================
// FUNCIONES PRINCIPALES
// ============================================================================

/**
 * Determina si un registro PTEL necesita validación cruzada.
 * 
 * ALGORITMO:
 * 1. Sin coordenadas → no se puede validar
 * 2. Infraestructura crítica → SIEMPRE validar
 * 3. Score < 70 → SIEMPRE validar
 * 4. Formato problemático (ODT/HTML) → validar
 * 5. Texto plano + score >= 90 → OMITIR
 * 6. Spreadsheet + score >= 88 → OMITIR
 * 7. Default → validar
 */
export function shouldCrossValidate(
  record: PTELRecordForValidation,
  config: ConditionalValidationConfig = DEFAULT_VALIDATION_CONFIG
): ValidationDecision {
  const factors: string[] = [];
  
  // 1. Sin coordenadas válidas
  if (!record.hasCoordinates && record.coordScore === undefined) {
    return {
      shouldValidate: false,
      reason: 'MISSING_DATA',
      explanation: 'No hay coordenadas para validar',
      priority: 3,
      factors: ['sin coordenadas'],
    };
  }
  
  const score = record.coordScore ?? 0;
  const formato = record.formato ?? 'plain';
  const tipologia = record.tipologia?.toUpperCase() ?? '';
  
  factors.push(`score: ${score}`);
  factors.push(`formato: ${formato}`);
  if (tipologia) factors.push(`tipología: ${tipologia}`);
  
  // 2. Infraestructura crítica → SIEMPRE validar
  if (config.alwaysValidateCritical && isCriticalInfrastructure(tipologia)) {
    return {
      shouldValidate: true,
      reason: 'CRITICAL_INFRASTRUCTURE',
      explanation: `Infraestructura crítica (${tipologia}) requiere validación exhaustiva`,
      priority: 1,
      factors: [...factors, 'crítica'],
    };
  }
  
  // 3. Score muy bajo → SIEMPRE validar
  if (score < config.alwaysValidateBelowScore) {
    return {
      shouldValidate: true,
      reason: 'LOW_SCORE',
      explanation: `Score ${score} está por debajo del umbral (${config.alwaysValidateBelowScore})`,
      priority: 1,
      factors: [...factors, 'score bajo'],
    };
  }
  
  // 4. Formato problemático → validar (si config lo indica)
  if (config.alwaysValidateProblematicFormats && isProblematicFormat(formato)) {
    return {
      shouldValidate: true,
      reason: 'PROBLEMATIC_FORMAT',
      explanation: `Formato ${formato} es problemático y requiere validación`,
      priority: 2,
      factors: [...factors, 'formato problemático'],
    };
  }
  
  // 5. Texto plano con score alto → OMITIR
  if (formato === 'plain' && score >= config.plainTextSkipScore) {
    return {
      shouldValidate: false,
      reason: 'HIGH_QUALITY_SKIP',
      explanation: `Texto plano con score alto (${score} >= ${config.plainTextSkipScore})`,
      priority: 3,
      factors: [...factors, 'alta calidad'],
    };
  }
  
  // 6. Spreadsheet con score alto → OMITIR
  if (formato === 'spreadsheet' && score >= config.spreadsheetSkipScore) {
    return {
      shouldValidate: false,
      reason: 'HIGH_QUALITY_SKIP',
      explanation: `Spreadsheet con score alto (${score} >= ${config.spreadsheetSkipScore})`,
      priority: 3,
      factors: [...factors, 'alta calidad'],
    };
  }
  
  // 7. Default → validar
  return {
    shouldValidate: true,
    reason: 'STANDARD_CHECK',
    explanation: 'Validación estándar recomendada',
    priority: 2,
    factors,
  };
}

/**
 * Verifica si es infraestructura crítica.
 */
export function isCriticalInfrastructure(tipologia: string): boolean {
  if (!tipologia) return false;
  const upper = tipologia.toUpperCase();
  return CRITICAL_INFRASTRUCTURE.some(critical => upper.includes(critical));
}

/**
 * Verifica si es un formato problemático.
 */
export function isProblematicFormat(formato: DocumentFormat): boolean {
  return PROBLEMATIC_FORMATS.includes(formato);
}

/**
 * Evalúa un lote de registros y calcula estadísticas de validación.
 */
export function evaluateBatch(
  records: PTELRecordForValidation[],
  config: ConditionalValidationConfig = DEFAULT_VALIDATION_CONFIG
): ValidationStats {
  const byReason: Record<ValidationDecisionReason, number> = {
    'CRITICAL_INFRASTRUCTURE': 0,
    'LOW_SCORE': 0,
    'PROBLEMATIC_FORMAT': 0,
    'HIGH_QUALITY_SKIP': 0,
    'STANDARD_CHECK': 0,
    'MISSING_DATA': 0,
  };
  
  let requireValidation = 0;
  let skipped = 0;
  
  for (const record of records) {
    const decision = shouldCrossValidate(record, config);
    byReason[decision.reason]++;
    
    if (decision.shouldValidate) {
      requireValidation++;
    } else {
      skipped++;
    }
  }
  
  const total = records.length;
  const savingsPercent = total > 0 ? Math.round((skipped / total) * 100) : 0;
  
  return {
    total,
    requireValidation,
    skipped,
    savingsPercent,
    byReason,
  };
}

/**
 * Filtra un lote de registros para obtener solo los que necesitan validación.
 */
export function filterForValidation<T extends PTELRecordForValidation>(
  records: T[],
  config: ConditionalValidationConfig = DEFAULT_VALIDATION_CONFIG
): { toValidate: T[]; toSkip: T[] } {
  const toValidate: T[] = [];
  const toSkip: T[] = [];
  
  for (const record of records) {
    const decision = shouldCrossValidate(record, config);
    if (decision.shouldValidate) {
      toValidate.push(record);
    } else {
      toSkip.push(record);
    }
  }
  
  return { toValidate, toSkip };
}

/**
 * Ordena registros por prioridad de validación.
 */
export function sortByValidationPriority<T extends PTELRecordForValidation>(
  records: T[],
  config: ConditionalValidationConfig = DEFAULT_VALIDATION_CONFIG
): T[] {
  return [...records].sort((a, b) => {
    const decisionA = shouldCrossValidate(a, config);
    const decisionB = shouldCrossValidate(b, config);
    return decisionA.priority - decisionB.priority;
  });
}

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  shouldCrossValidate,
  isCriticalInfrastructure,
  isProblematicFormat,
  evaluateBatch,
  filterForValidation,
  sortByValidationPriority,
  DEFAULT_VALIDATION_CONFIG,
  AGGRESSIVE_SKIP_CONFIG,
  CONSERVATIVE_CONFIG,
  PROBLEMATIC_FORMATS,
};
